// Controls
import { useTheme } from "@fluentui/react";
import { ContentEdit, HyperLink, Paste, Watermark } from "roosterjs-editor-plugins";
import { createEmojiPlugin, createRibbonPlugin, createUpdateContentPlugin, UpdateMode } from "roosterjs-react/lib-mjs";
import { ScreenReaderOnly } from "../../screenReaderOnly/ScreenReaderOnly";
import { LazyRichTextEditorRibbon } from "./LazyRichTextEditorRibbon";
import { EditorAdapter } from "roosterjs-editor-adapter";
import { getDarkColor as getDarkColorV9 } from "roosterjs-v9";
import { Rooster as RoosterV9 } from "roosterjs-react-v9";
// Styles
import { getClassNames, getStyles, richTextEditorWatermarkClassName } from "./RichTextEditor.styles";
// Utilities
import { css, getRTL } from "@fluentui/utilities";
import { ErrorUtilities } from "@ms/uno-errors/lib/local/utilities/ErrorUtilities";
import { safelyOpenExternalUrl } from "@ms/uno-utilities/lib/local/BrowserUtilities";
import { sanitizeContent, sanitizeForTrustedTypes } from "@ms/uno-utilities/lib/local/ContentSanitizationUtilities";
import { createTrustedHtml } from "@ms/uno-utilities/lib/local/TrustedTypeUtilities";
import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { RoosterBlockDnDPlugin } from "./plugins/RoosterBlockDnDPlugin";
import { RoosterContentLimitPlugin } from "./plugins/RoosterContentLimitPlugin";
import { RoosterGetEditorRefPlugin } from "./plugins/RoosterGetEditorRefPlugin";
import { RoosterPasteCleanupPlugin } from "./plugins/RoosterPasteCleanupPlugin";
import { RoosterScrollPreserverOnCopyPlugin } from "./plugins/RoosterScrollPreserverOnCopyPlugin";
// Logging
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
import { useLoggers } from "../../../hooks/useLoggers";
/**
 * Max height for editor content preview
 */ const PreviewMaxHeightPx = 100;
/**
 * Line break constant
 */ const LineBreak = "<br>";
export function RichTextEditor({ initializeUpdateContentPlugin, initializeWatermarkPlugin, initializeBlockImagePastePlugin, initializeBlockDnDPlugin, initializeContentLimitPlugin, initializeEmojiPlugin, initializeHyperLinkPlugin, initializeContentEditPlugin, initializePasteContentPlugin, initializeRibbonPlugin, isReadOnly, disableCollapseExpanBehavior, ariaLabelContent, value, placeholder, placeholderStyle, darkTheme, maxLength, autoFocus, previewMaxHeight, lockContent, showLessString, showMoreString, onFocus, onBlur, onMiscUserAction, onChange, scrollContainerRef, ...props }) {
    // State
    const [isFocusOnEditor, setIsFocusOnEditor] = useState(false);
    const [isEditorExpanded, setIsEditorExpanded] = useState(false);
    const [contentExceedsPreview, setContentExceedsPreview] = useState(false);
    const [showRibbon, setShowRibbon] = useState(false);
    const theme = useTheme();
    // Refs
    const pluginsInitialized = useRef(false);
    const roosterPlugins = useRef([]);
    const roosterV9Plugins = useRef([]);
    const ribbonPlugin = useRef();
    const getEditorRefPlugin = useRef();
    const scrollPreserverPlugin = useRef(null);
    const ignoreNextNotesContentChangedEvent = useRef(false);
    const notesEditorWrapperRef = useRef(null);
    const noteEditorRibbonRef = useRef(null);
    const prevPropsContentValueRef = useRef(value);
    // Get trace logger
    const { traceLogger } = useLoggers();
    // Get the styles for this component
    const styles = ()=>{
        const preview = !isFocusOnEditor && !isEditorExpanded && !disableCollapseExpanBehavior;
        const moreContent = !isFocusOnEditor && !isEditorExpanded && contentExceedsPreview && !disableCollapseExpanBehavior;
        const isFocused = isFocusOnEditor;
        const readOnly = isReadOnly ?? false;
        const styleProps = {
            readOnly,
            preview,
            moreContent,
            isFocused
        };
        return getStyles(styleProps, theme, props.styles);
    };
    const sanitizedValue = React.useMemo(()=>{
        if (!value) {
            return value;
        }
        return isReadOnly ? sanitizeContent(value, {
            openLinksInNewTab: true
        }) : sanitizeContent(value);
    }, [
        isReadOnly,
        value
    ]);
    const classNames = getClassNames(styles());
    const onLinkClick = useCallback((anchor, event)=>{
        // Currently  links on simple click in RTE do not open. Only Ctrl + Click.
        // This code was proposed by roosterJS team to address that issue as they had the same issue with the demo:
        // https://github.com/microsoft/roosterjs/pull/1460/files
        let href;
        function tryGetHref(anchor) {
            try {
                return anchor ? anchor.href : null;
            } catch  {
                return null;
            }
        }
        if ((href = tryGetHref(anchor)) && event.button === 0) {
            event.preventDefault();
            try {
                safelyOpenExternalUrl(href, traceLogger, "_blank");
                return true;
            } catch  {
                return false;
            }
        }
        return false;
    }, [
        traceLogger
    ]);
    if (!pluginsInitialized.current) {
        // Ribbon plugin allows us to add custom buttons to the ribbon
        if (initializeRibbonPlugin) {
            ribbonPlugin.current = createRibbonPlugin();
            roosterPlugins.current.push(ribbonPlugin.current);
        }
        // Emoji plugin allows adding emojis by typing :
        initializeEmojiPlugin && roosterPlugins.current.push(createEmojiPlugin());
        // Content update plugin allows us to periodically update the notes
        initializeUpdateContentPlugin && roosterPlugins.current.push(createUpdateContentPlugin(UpdateMode.OnDispose, (newValue)=>{
            if (ignoreNextNotesContentChangedEvent.current) {
                ignoreNextNotesContentChangedEvent.current = false;
                return;
            }
            onChange(newValue);
        }));
        // Paste plugin controls rendering the placeholder
        // Paste plugin, handles BeforePaste event and reformat some special content, including:
        // 1. Content copied from Word
        // 2. Content copied from Excel
        // 3. Content copied from Word Online or Onenote Online
        initializePasteContentPlugin && roosterPlugins.current.push(new Paste());
        // Watermark plugin controls rendering the placeholder
        initializeWatermarkPlugin && roosterPlugins.current.push(new Watermark(placeholder, placeholderStyle, richTextEditorWatermarkClassName));
        // Hyperlink plugin controls rendering the placeholder.
        // See https://github.com/microsoft/roosterjs/wiki/HyperLink-plugin#parameters for more details.
        initializeHyperLinkPlugin && roosterPlugins.current.push(new HyperLink(/** getTooltipCallback */ undefined, /** target */ "_blank", /** onLinkClick */ onLinkClick));
        // Content Edit plugin allows to recognize links
        initializeContentEditPlugin && roosterPlugins.current.push(new ContentEdit());
        // Paste cleanup (also gated by initializeBlockImagePastePlugin): strips pasted <img>
        // tags AND normalizes <ul>/<ol> formatting so bullet markers stay visible after the
        // RoosterJS sanitizer strips default styles. Drag-and-drop blocking is gated separately.
        initializeBlockImagePastePlugin && roosterPlugins.current.push(new RoosterPasteCleanupPlugin());
        initializeBlockDnDPlugin && roosterPlugins.current.push(new RoosterBlockDnDPlugin());
        // Plugin that limits the content length
        initializeContentLimitPlugin && roosterPlugins.current.push(new RoosterContentLimitPlugin(maxLength));
        // Clipboard plugin to preserve scroll during copy/cut events
        scrollPreserverPlugin.current = new RoosterScrollPreserverOnCopyPlugin();
        roosterPlugins.current.push(scrollPreserverPlugin.current);
        // Store a reference to getEditorRefPlugin so we can use it to get a ref to the editor in order to set content on task change
        getEditorRefPlugin.current = new RoosterGetEditorRefPlugin();
        roosterPlugins.current.push(getEditorRefPlugin.current);
        pluginsInitialized.current = true;
    }
    /**
     * Return whether the editor field is taller than the preview max height
     */ const isEditorContentOverflownVertically = useCallback(()=>{
        const maxHeight = previewMaxHeight ? Number(previewMaxHeight) : PreviewMaxHeightPx;
        return !!((notesEditorWrapperRef?.current?.firstElementChild?.clientHeight ?? -1) > maxHeight);
    }, [
        previewMaxHeight
    ]);
    useEffect(()=>{
        // Need the setTimeout for notes to actually be rendered first
        setTimeout(()=>{
            // Once task details mounts, determine if notes content exceeds the preview height
            isEditorContentOverflownVertically() && setContentExceedsPreview(true);
        }, 0);
        const editorRef = getEditorRefPlugin.current?.getEditorRef();
        if (editorRef && autoFocus) {
            editorRef.focus();
        }
    }, [
        isEditorContentOverflownVertically,
        autoFocus
    ]);
    useEffect(()=>{
        const editorRef = getEditorRefPlugin.current?.getEditorRef();
        if (editorRef && autoFocus) {
            editorRef.focus();
        }
        if (prevPropsContentValueRef.current !== value) {
            // If the value changes and the notes are expanded, collapse the notes
            if (isEditorExpanded && !disableCollapseExpanBehavior) {
                setIsEditorExpanded(false);
            }
            // If the note has changed and the user isn't editing the notes field, set the editor content to the new note
            if (editorRef && !lockContent) {
                // Ignore the next content change since this setContent will trigger it
                ignoreNextNotesContentChangedEvent.current = true;
                // This needs to be set before setContent as the setContent may trigger cursor reset
                const editorRange = editorRef.getSelectionRange();
                const cursorPosition = editorRange?.startOffset;
                editorRef.setContent(sanitizedValue || LineBreak);
                // Maintain cursor position in Co-Auth
                if (isFocusOnEditor) {
                    try {
                        const notesTextNode = editorRange?.startContainer?.firstChild?.firstChild;
                        if (notesTextNode && cursorPosition != null && editorRange) {
                            editorRange.setStart(notesTextNode, getUpdatedCursorPosition(notesTextNode, cursorPosition));
                        }
                    } catch (e) {
                        traceLogger.logTrace(0x1e44e806 /* tag_4ro6g */ , TraceLevel.Error, `Error updating notes cursor position: [ErrorMessage=${ErrorUtilities.getMessage(e)}]`);
                    }
                }
            }
            // Determine if the notes content exceeds the preview height
            if (notesEditorWrapperRef.current) {
                const exceedsPreview = isEditorContentOverflownVertically();
                if (contentExceedsPreview !== exceedsPreview) {
                    setContentExceedsPreview(exceedsPreview);
                }
            }
        }
    }, [
        autoFocus,
        contentExceedsPreview,
        disableCollapseExpanBehavior,
        isEditorContentOverflownVertically,
        isEditorExpanded,
        isFocusOnEditor,
        lockContent,
        value,
        traceLogger,
        sanitizedValue
    ]);
    useEffect(()=>{
        prevPropsContentValueRef.current = value;
    }, [
        value
    ]);
    // Set the scroll container reference in the plugin when it changes
    useEffect(()=>{
        if (scrollPreserverPlugin.current && scrollContainerRef?.current) {
            scrollPreserverPlugin.current.setScrollContainer(scrollContainerRef.current);
        }
    }, [
        scrollContainerRef
    ]);
    /**
     * Handles notes focus event
     * @param event React focus event
     */ const onNotesFocus = useCallback((event)=>{
        onFocus?.(event);
        setIsFocusOnEditor(true);
        setShowRibbon(true);
    }, [
        onFocus
    ]);
    /**
     * Handles notes blur event
     * @param event React focus event
     */ const onNotesBlur = useCallback((event)=>{
        // Save the latest task notes
        const editorRef = getEditorRefPlugin.current?.getEditorRef();
        let editorRefContent = "";
        if (editorRef) {
            editorRefContent = editorRef.getContent();
            /**
                 * If the task note has only spaces then it is equivalent of not having any notes
                 * hence update the task note with no note which is <br>
                 */ if (editorRef.getContent()?.trim()?.length === 0) {
                editorRefContent = LineBreak;
            }
        }
        const currentEditorRef = editorRef ? editorRefContent : LineBreak;
        onBlur?.(event, currentEditorRef);
        if (!noteEditorRibbonRef.current?.contains(event.relatedTarget)) {
            setShowRibbon(false);
        }
        if (notesEditorWrapperRef?.current?.firstElementChild) {
            setIsFocusOnEditor(false);
            setContentExceedsPreview(isEditorContentOverflownVertically());
        }
    }, [
        getEditorRefPlugin,
        isEditorContentOverflownVertically,
        onBlur
    ]);
    const renderRibbonButtons = ()=>{
        if (!initializeRibbonPlugin) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("div", {
            ref: noteEditorRibbonRef,
            className: classNames.ribbonContainer
        }, showRibbon && ribbonPlugin.current && /*#__PURE__*/ React.createElement(Suspense, {
            fallback: /*#__PURE__*/ React.createElement(React.Fragment, null)
        }, /*#__PURE__*/ React.createElement(LazyRichTextEditorRibbon, {
            plugin: ribbonPlugin.current
        })));
    };
    const editorCreator = useCallback((div, options)=>{
        return new EditorAdapter(div, {
            ...options ?? {},
            legacyPlugins: roosterPlugins.current,
            initialContent: sanitizedValue
        });
    }, [
        sanitizedValue
    ]);
    // Handles showing more/show less editor notes
    const onNotesViewChange = (event)=>{
        onMiscUserAction();
        if (!disableCollapseExpanBehavior) {
            setIsEditorExpanded(!isEditorExpanded);
        }
    };
    /**
     * Renders the editor show more / less button if necessary. Otherwise returns null
     */ const renderNotesShowButton = ()=>{
        if (isFocusOnEditor || !contentExceedsPreview || disableCollapseExpanBehavior) {
            return null;
        }
        return /*#__PURE__*/ React.createElement("button", {
            className: classNames.expandNotesButton,
            onClick: onNotesViewChange
        }, isEditorExpanded ? showLessString : showMoreString);
    };
    // Preserve HTML comments as RoosterJS internally rely on it for cut/copy operations, this is not needed in newer versions of RoosterJS
    // See https://github.com/microsoft/roosterjs/issues/2289
    const trustedHTMLHandler = (content)=>sanitizeForTrustedTypes(content, {
            allowedTags: [
                "#comment"
            ]
        });
    return /*#__PURE__*/ React.createElement("div", {
        className: classNames.root
    }, /*#__PURE__*/ React.createElement(ScreenReaderOnly, null, /*#__PURE__*/ React.createElement("label", {
        id: "notes-label"
    }, ariaLabelContent)), /*#__PURE__*/ React.createElement("div", {
        className: classNames.editorWrapper,
        ref: notesEditorWrapperRef
    }, isReadOnly ? /* eslint-disable react/no-danger */ /*#__PURE__*/ React.createElement("div", {
        "aria-labelledby": "notes-label",
        className: css(classNames.notesEditor, "read-only"),
        // Apply link targets during sanitization so serialized HTML is never modified after the security boundary.
        dangerouslySetInnerHTML: {
            __html: createTrustedHtml(sanitizedValue || placeholder, true)
        }
    }) : /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(RoosterV9, {
        "aria-labelledby": "notes-label",
        className: classNames.notesEditor,
        dir: getRTL() ? "rtl" : "ltr",
        contentEditable: !isReadOnly,
        plugins: roosterV9Plugins.current,
        onFocus: onNotesFocus,
        onBlur: onNotesBlur,
        inDarkMode: darkTheme,
        getDarkColor: getDarkColorV9,
        trustedHTMLHandler: trustedHTMLHandler,
        role: "textbox",
        "aria-placeholder": placeholder,
        editorCreator: editorCreator
    }), renderRibbonButtons())), renderNotesShowButton());
}
/**
 * Update cursor position if needed, based on text content
 * @param notesTextNode Child node that contains the notes text
 * @param cursorPosition Number that represents position of the cursor
 * @returns updated cursor position
 */ export function getUpdatedCursorPosition(notesTextNode, cursorPosition) {
    // If text has been deleted past the cursorPosition, reposition cursor to end of text
    if (cursorPosition > notesTextNode?.textContent?.length) {
        cursorPosition = notesTextNode.textContent.length;
    }
    return cursorPosition;
}
